<template>
  <div
    class="ux-tabset"
    :class="{
      vertical,
      horizontal: !vertical,
      inline,
      block: !inline && !pills,
      pills,
    }"
  >
    <template v-if="vertical">
      <flex-column v-if="menuRequired" class="tabset-menu">
        <flex-body>
          <a
            :class="{ active: activeHeading === key }"
            :key="`tab-link-${key}`"
            v-for="(key, index) in keys"
            @click="select(key)"
            >{{ titles[index] }}</a
          >
        </flex-body>
      </flex-column>
    </template>
    <template v-else-if="pills">
      <flex-header v-if="menuRequired" class="tabset-menu">
        <flex-row>
          <ux-button
            :color="activeHeading === key ? 'primary' : ''"
            :key="`tab-pill-${key}`"
            v-for="(key, index) in keys"
            @click="select(key)"
            >{{ titles[index] }}</ux-button
          >
        </flex-row>
      </flex-header>
    </template>
    <template v-else>
      <flex-header v-if="menuRequired" class="tabset-menu">
        <flex-row ref="menuRow">
          <a
            :class="{ active: activeHeading === key }"
            :key="`tab-link-${key}`"
            v-for="(key, index) in keys"
            @click="select(key)"
            >{{ titles[index] }}</a
          >
        </flex-row>
      </flex-header>
    </template>
    <flex-column>
      <slot />
    </flex-column>
  </div>
</template>
<script setup>
import {
  useSlots,
  ref,
  computed,
  reactive,
  provide,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from "vue";
import _debounce from "lodash/debounce";

const slots = useSlots();

const props = defineProps({
  vertical: Boolean,
  inline: Boolean,
  pills: Boolean,
  defaultTab: String, // Tab key or heading to set as default active tab
});

const tabset = reactive({
  tabs: [],
});
provide("tabset", tabset);

const activeHeading = ref();
provide("activeHeading", activeHeading);

const select = function (heading) {
  activeHeading.value = heading;
};

const rebuild = _debounce(function () {
  const items = slots.default();
  const filtered = items
    .reduce(function (memo, tab) {
      if (tab.type?.name === "tab") {
        memo.push(tab);
      } else if (tab.children) {
        memo = [...memo, ...tab.children];
      }

      return memo;
    }, [])
    .filter(function (tab) {
      return tab.type?.name === "tab" && tab.props.enabled !== false;
    })
    .map(function (tab, index) {
      const heading = tab.props.heading;
      const key = tab.props.tabKey || heading;

      return {
        heading,
        key,
        index,
      };
    });

  tabset.tabs = filtered;

  const lookup = filtered.reduce(function (memo, t) {
    memo[t.key || t.heading] = true;
    return memo;
  }, {});

  const existingSelectedHeading = activeHeading.value;

  if (!existingSelectedHeading || !lookup[existingSelectedHeading]) {
    if (filtered.length) {
      let headingKey;

      // Check if defaultTab prop is provided and exists in the tabs
      if (props.defaultTab && lookup[props.defaultTab]) {
        headingKey = props.defaultTab;
      } else {
        // Fall back to first tab if defaultTab is not specified or doesn't exist
        headingKey = filtered[0].key || filtered[0].heading;
      }

      activeHeading.value = headingKey;
    }
  }
});

provide("rebuild", rebuild);

const titles = computed(function () {
  return tabset.tabs
    .map(function (tab) {
      if (!tab) {
        return;
      }
      return tab.heading;
    })
    .filter(Boolean);
});

const keys = computed(function () {
  return tabset.tabs
    .map(function (tab) {
      if (!tab) {
        return;
      }
      return tab.key;
    })
    .filter(Boolean);
});

const menuRequired = computed(function () {
  return titles.value.length > 1;
});

// Sliding active-tab indicator. CSS can't measure variable-width tabs, so we
// feed the active tab's position/width to CSS as custom properties and let it
// transition the underline between tabs.
const menuRow = ref(null);

function updateIndicator() {
  nextTick(function () {
    const row = menuRow.value && menuRow.value.$el;
    if (!row) return;

    const active = row.querySelector("a.active");
    if (!active) {
      row.style.removeProperty("--ind-width");
      return;
    }

    row.style.setProperty("--ind-left", `${active.offsetLeft}px`);
    row.style.setProperty("--ind-width", `${active.offsetWidth}px`);
  });
}

watch(activeHeading, updateIndicator);
watch(titles, updateIndicator);

onMounted(function () {
  updateIndicator();
  window.addEventListener("resize", updateIndicator);
  if (document.fonts && document.fonts.ready) {
    // Tab widths can shift once web fonts finish loading.
    document.fonts.ready.then(updateIndicator);
  }
});

onBeforeUnmount(function () {
  window.removeEventListener("resize", updateIndicator);
});

// const slotItems = computed(function() {
//     const slots =  slots.default().filter(function(tab) {
//         return tab.type?.name === 'tab';
//     }).map(function(tab) {
//         return {
//             title: tab.props?.heading || tab.props?.title || 'Untitled Tab',
//             self: tab,
//             //         active:(tab.props?.active !== undefined),
//             //         enabled:tab.props?.enabled,
//         }
//     })

//     return slots;
// })

// // Get all of the tab titles
// slotItems.map(function(slot) {

//     return slot?.heading;
// }))

// const defaultTab = slotItems[0];
// const activeKey = defaultTab?.title;

// const activeTitle = ref(activeKey);
// provide('activeTitle', activeTitle)

// const select = function(title) {
// activeTitle.value = title;
// }
// provide('select', select)

rebuild();
</script>
<style lang="scss" scoped>
.ux-tabset {
  flex: 1;
  overflow: hidden;

  &.horizontal {
    display: flex;
    flex-direction: column;

    &.block {
      > .tabset-menu {
        background: color-mix(in lab, currentColor 7%, transparent);

        a {
          display: block;
          padding: 1em;
          cursor: pointer;
          color: color-mix(in lab, currentColor, transparent);
          font-weight: 500;
          border-top: 3px solid transparent;
          text-align: center;
          flex: 1;
          align-items: center;

          &:hover {
            color: color-mix(in lab, currentColor 60%, transparent);
          }

          &.active {
            background: var(--bg1, #fff);
            color: var(--primary);
            border-top: 3px solid var(--primary);
          }
        }
      }
    }

    &.inline {
      > .tabset-menu {
        overflow-x: auto;

        .flex-row {
          position: relative;
        }

        // Sliding active-tab underline. --ind-left / --ind-width are set by
        // the script as the active tab changes, so the line can transition
        // between tabs of any width.
        .flex-row::after {
          content: "";
          position: absolute;
          bottom: 0;
          left: var(--ind-left, 0);
          width: var(--ind-width, 0);
          height: 3px;
          background: var(--primary);
          border-radius: 3px 3px 0 0;
          transition:
            left 0.25s ease,
            width 0.25s ease;
          pointer-events: none;
        }

        a {
          display: block;
          padding: 1em 0;
          margin: 0 1.5em 0 0;
          cursor: pointer;
          opacity: 0.5;
          font-weight: 500;
          border-bottom: 3px solid transparent;
          text-align: center;
          display: inline-block;
          white-space: nowrap;
          text-overflow: ellipsis;

          &.active {
            opacity: 1;
          }
        }
      }
    }

    &.pills {
      > .tabset-menu {
        overflow-x: auto;
      }
    }
  }

  &.vertical {
    display: flex;
    flex-direction: row;

    > .tabset-menu {
      max-width: 150px;
      min-width: 100px;
      background: color-mix(in lab, currentColor 7%, transparent);

      a {
        display: block;
        padding: 1em;
        cursor: pointer;
        color: color-mix(in lab, currentColor, transparent);
        font-weight: 500;
        border-left: 3px solid transparent;

        &:hover {
          color: color-mix(in lab, currentColor 60%, transparent);
        }

        &.active {
          background: var(--bg1, #fff);
          color: var(--primary);
          border-left: 3px solid var(--primary);
        }
      }
    }
  }
}
</style>
